import type { CSSProperties } from 'react'

const PALETTES = {
  terra: ['#C26345', '#E89770', '#F2D9A8'],
  felt: ['#3F6E4E', '#7AAA7E', '#DCE8C6'],
  gilt: ['#B98427', '#D9B068', '#F5E2B3'],
  plum: ['#7A4F6E', '#B284A0', '#E8D2DA'],
  slate: ['#3D5366', '#7B91A3', '#CFD8DD'],
  brick: ['#8E3A2E', '#C9745F', '#F0CDB6'],
} as const

export type GameThumbProps = {
  label: string
  size?: number
  palette?: keyof typeof PALETTES
  radius?: number
  // When provided, render this image (e.g. BGG cover) on top of the procedural
  // background. The procedural design serves as the fallback / loading state.
  imageUrl?: string | null
  style?: CSSProperties
}

const paletteFromName = (name: string): keyof typeof PALETTES => {
  const keys = Object.keys(PALETTES) as Array<keyof typeof PALETTES>
  const sum = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return keys[sum % keys.length]!
}

export const GameThumb = ({
  label,
  size = 56,
  palette,
  radius = 4,
  imageUrl,
  style,
}: GameThumbProps) => {
  const key = palette ?? paletteFromName(label)
  const [a, b, c] = PALETTES[key]
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: `linear-gradient(135deg, ${a} 0%, ${a} 38%, ${b} 38%, ${b} 62%, ${c} 62%, ${c} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        boxShadow: '0 1px 2px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.08) inset',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 4px, transparent 4px 9px)',
        }}
      />
      {!imageUrl && (
        <div
          style={{
            position: 'absolute',
            left: 4,
            bottom: 3,
            right: 4,
            fontFamily: 'var(--font-mono)',
            fontSize: Math.max(7, size * 0.13),
            color: 'rgba(255,250,240,0.92)',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {label}
        </div>
      )}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  )
}
